import { useEffect, useMemo, useState } from "react";
import { usePreferencesStore } from "@/stores/usePreferencesStore";
import { applyTheme, resolveTheme, ThemeContext, type ResolvedTheme, type ThemeContextValue } from "./ThemeContext";

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const mode = usePreferencesStore((state) => state.theme);
  const saveTheme = usePreferencesStore((state) => state.saveTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(resolveTheme(mode));

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const updateResolvedTheme = () => setResolvedTheme(resolveTheme(mode));

    updateResolvedTheme();
    mediaQuery.addEventListener("change", updateResolvedTheme);

    return () => {
      mediaQuery.removeEventListener("change", updateResolvedTheme);
    };
  }, [mode]);

  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      resolvedTheme,
      setMode: (nextMode) => {
        void saveTheme(nextMode);
      },
      toggleTheme: () => {
        const nextMode = resolveTheme(mode) === "dark" ? "light" : "dark";
        void saveTheme(nextMode);
      },
    }),
    [mode, resolvedTheme, saveTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
